/**
 * The run's three phases — Extract, Plan, Send — derived from ONE payload.
 *
 * The platform reports a single `state` string for the whole run, so "Extract settled, Plan settled,
 * Send live" is an inference, not a reading. It is inferred from METRICS and `status`, never by
 * matching `state` against known strings: rudder-sources has six working phases and only three were
 * ever observed by polling, so a closed list would silently mis-place the other three. This
 * derivation is default-forward — an unrecognised `state` changes nothing about position, and the
 * string is rendered verbatim wherever it belongs.
 *
 * Metrics are NOT per-phase. Each phase WRITES one group — there are five (`METRIC_GROUPS` in
 * `run-fixtures.test.ts`, not `run-fixtures.ts`: `changed`, `invalid`, `successful`, `failed`,
 * `dropped`; `total` is a field, not a group) — and during extract every one of them reads 0, but
 * that is NOT byte-identical to a nothing-to-do run: `liveExtract.metrics.total` is `0`, `settledNothingToDo.metrics.total` is `20`.
 * For a live run it is `total`, not `status`, that separates "hasn't counted yet" from "counted a real
 * zero" — which is exactly what `isCounted` keys on.
 *
 * `'unknown'` (an unrecognised platform status — `runStatus`'s deliberately-not-a-success catch-all)
 * is treated exactly like `'failed'` here, same as `runSummary`'s `stopped` local in `run-view.ts`. A
 * status this build does not recognise (e.g. an aborted run) must not read as a clean completion: the
 * phase it stopped in renders `failed`, earlier phases `settled`, later phases `idle`, and the
 * platform's `state` string belongs to the stopped phase alone. Were `'unknown'` to fall through to
 * `activities`' settled branch, both legs of the spine would read `done` and the state string would
 * leave the stage entirely — a settled phase carries none (see `stateFor`).
 */
import type { RawSyncRun } from '../types';
import { exact, group, isCounted, isMeasured, isRunning, runStatus, validRecords } from './run-view';

export type PhaseName = 'extract' | 'plan' | 'send';
export type PhaseActivity = 'idle' | 'live' | 'settled' | 'failed';

/**
 * The state of one EDGE of the spine — the segment between two marks. There are exactly two edges
 * (Extract->Plan and Plan->Send) and this is where run state is drawn; see `PhaseStage`'s header for
 * why two edges rather than three dots.
 *
 * `unreached` is not a colourless `done`. "This never started" and "this finished" are different
 * facts about the pipeline and must not collapse into one grey hairline, which is exactly what a
 * stopped run would otherwise look like below the point it stopped at.
 */
export type LegState = 'working' | 'done' | 'failed' | 'unreached';

export interface RunPhase {
  phase: PhaseName;
  activity: PhaseActivity;
  /** Value, denominator and the two unit words for `RunDenominated`. */
  n: number;
  d: number;
  /** Names the DENOMINATOR: `of 85,324 returned`. */
  unit: 'returned' | 'valid' | 'changed';
  /**
   * Names the NUMERATOR: `84,120 valid`. THE CHAIN IS THE POINT — each row's `numeratorUnit` is the
   * next row's `unit`, so reading the first noun down the stack gives valid -> changed -> accepted,
   * which is the pipeline. That chain does the work the deleted phase captions used to do (see
   * `PhaseStage`'s "NO PHASE LABELS ON SCREEN"), and `run-phases.test.ts` pins it across every fixture
   * rather than trusting three literals to stay in step.
   */
  numeratorUnit: 'valid' | 'changed' | 'accepted';
  /**
   * Whether the RUN has started measuring yet — `isCounted`, run-wide, identical on all three
   * phases. Feeds `RunDenominated`'s `counted`, whose `false` word ("counting") is PROGRESSIVE: it
   * says wait, and waiting resolves it.
   *
   * It is NOT "are n/d printable". The `-1` count-query-timeout sentinel is the other, TERMINAL way
   * a number can be absent, and it does not belong here — a settled run whose count query timed out
   * is not counting and never will be. `RunDenominated` refuses that value at both ends of the
   * fraction (negative numerator and negative denominator alike) and says so in different words.
   * See `deriveRunPhases`'s Plan block.
   */
  counted: boolean;
  /** `nulls 802 · duplicates 402` / `new 1,230 · updated 0 · deleted 0` / `failed 12 · dropped 3`. Empty when idle. */
  breakdown: string;
  /** The platform's own state string, verbatim, or '' when it does not belong to this phase. */
  state: string;
  /**
   * The two halves of the spine this row draws, and the reason a leg is never two colours.
   *
   * A LEG IS SHARED BETWEEN TWO ROWS: the segment between mark N and mark N+1 is row N's `spine-out`
   * plus row N+1's `spine-in`, drawn by two different `PhaseStage` renders that never see each other.
   * Two independent derivations of "what colour is this segment" would disagree the moment one of
   * them changed. So the deriver computes the two leg states ONCE, in `legStates()`, and hands the
   * same value to both rows that draw a piece of it — `legOut` on the upper row and `legIn` on the
   * lower row are the identical string by construction, not by two rules that happen to agree.
   *
   * `null` means there is no leg on that side: nothing above the first mark, nothing below the last.
   * It is not a state; a row with `legIn: null` draws no incoming segment at all.
   */
  legIn: LegState | null;
  legOut: LegState | null;
  /**
   * Only meaningful when `activity === 'idle'`, and `null` otherwise — the two different facts a bare
   * "not started" used to flatten into one:
   *
   *   pending  the run is still going and this phase is genuinely coming
   *   void     the run already stopped; this phase never happened and never will
   *
   * The discriminator is `failedPhase(run) !== null` — i.e. whether `activities()` placed a failure
   * anywhere — computed off the same `activities()` call the rest of this deriver uses rather than a
   * second one. A settled run has no idle phase at all, so only a running or a stopped run reaches
   * either arm.
   *
   * The `void` arm is REACHABLE, and only became so once `activePhase()` stopped asking `isCounted()`
   * (which is true for every non-running run and so placed every failure in Send). A run that died
   * before reading a row now legitimately produces two void rows below the Extract it died in —
   * previously those two rendered as SETTLED at `0 of 0`, claiming completions that never happened.
   */
  idleKind: 'pending' | 'void' | null;
}

/**
 * Which phase the run is currently in — a run that has counted rows is in Send, everything else is
 * in Extract. Plan is therefore never the active phase.
 *
 * Reached only for a RUNNING or a STOPPED run: `activities()` short-circuits a cleanly settled
 * one before it gets here. For a stopped run this is the failure's location, which is why it
 * must read the metric and not `isCounted` — see the note on the return.
 *
 * THE OBSERVATION: no polled payload has ever shown Plan live on its own. That is two samples of one
 * run (`metrics.total` reads 0 at t=0 and 1,230 at t=+0.2s — see `isCounted`'s own doc in
 * `run-view.ts`), which spans more than one phase and so pins where `total` changed, nothing about
 * what runs between. It is not a mechanism this function or that one may assume, and the sentence
 * that used to follow it here ("a run that has counted anything is past both Extract and Plan")
 * asserted exactly the mechanism the sentence before it disclaimed.
 *
 * THE KNOWN BLIND SPOT, stated plainly: `metrics.total` is written by the extract phase and
 * `metrics.changed` by the diff that follows it, so a live run polled after `total` lands but before
 * `changed` does reads as SETTLED at Plan — `isCounted` is already true, the run is placed in Send,
 * so leg 1 reads `done` and Plan announces "Completed" for a diff still in flight. No
 * fixture in `run-fixtures.ts` has that shape (every running fixture is either `total: 0` or
 * `changed.total === total`), which is precisely why this is written down rather than tested.
 *
 * Position is deliberately NOT changed to cover it: doing so needs evidence from the producer that
 * nobody has, and it would risk the cases that ARE observed. What the window does get is a guard on
 * the one thing it could FABRICATE — Plan's operation split; see the `midPlanWindow` local in
 * `deriveRunPhases`.
 */
function activePhase(run: RawSyncRun): PhaseName {
  // `metrics.total > 0` DIRECTLY, not `isCounted(run)`. They agree on a running run — `isCounted`
  // tests exactly this there — but `isCounted` short-circuits to `true` for ANY non-running run,
  // which is right for its own question ("is this run measuring yet", the `counted` prop) and wrong
  // for this one. Through that short-circuit every STOPPED run resolved to `send` no matter what it
  // had counted, so: `failedPhase()` was a constant, the error sentence said "Send failed" for a run
  // that died in extract, and — worst — `activities()` rendered Extract and Plan as SETTLED at
  // `0 of 0` for a run that never returned a row. Two rows claiming a completion that did not happen.
  // Verified: a failed run with `total: 0`, with no metrics at all, and with `total: 1230` all
  // resolved to `send` before this change.
  return (run.metrics?.total ?? 0) > 0 ? 'send' : 'extract';
}

function activities(run: RawSyncRun): Record<PhaseName, PhaseActivity> {
  const order: PhaseName[] = ['extract', 'plan', 'send'];
  const status = runStatus(run);
  // Treat an unrecognised status exactly like a failed one — see this module's header. A single local
  // rather than repeating the disjunction in both `activities` and `stateFor`.
  const stopped = status === 'failed' || status === 'unknown';

  // A settled run has been through everything it was going to go through.
  if (!isRunning(run) && !stopped) {
    return { extract: 'settled', plan: 'settled', send: 'settled' };
  }

  const active = activePhase(run);
  const activeIndex = order.indexOf(active);
  const activeActivity: PhaseActivity = stopped ? 'failed' : 'live';

  return order.reduce(
    (acc, phase, index) => {
      acc[phase] = index < activeIndex ? 'settled' : index === activeIndex ? activeActivity : 'idle';
      return acc;
    },
    {} as Record<PhaseName, PhaseActivity>,
  );
}

/**
 * The state of the two spine edges, derived from `activities()` and from nothing else — there is no
 * second source of truth about where the run is, and a leg that disagreed with the phases it joins
 * would be one.
 *
 * WHY TWO LEGS AND NOT THREE DOTS: `activePhase()` returns only `extract` or `send`, so the payload
 * distinguishes exactly two live positions. Three dots always claimed a third the wire cannot
 * support. Leg 1 spans Extract AND Plan, which is why its label ("Preparing your audience", in
 * `PhaseStage`) has to be true of both halves — anything naming the warehouse would stop being true
 * the moment the diff starts.
 *
 * Precedence is failed, then working, then done. A leg touching a failed phase is failed even if its
 * other end settled: the run stopped somewhere on that segment. `unreached` is the fall-through, and
 * covers every leg with an `idle` end — the two below a run that died in Extract, and the two below a
 * run that is still in Extract.
 */
function legStates(act: Record<PhaseName, PhaseActivity>): [LegState, LegState] {
  const leg = (from: PhaseActivity, to: PhaseActivity): LegState => {
    if (from === 'failed' || to === 'failed') return 'failed';
    if (from === 'live' || to === 'live') return 'working';
    if (from === 'settled' && to === 'settled') return 'done';
    return 'unreached';
  };
  return [leg(act.extract, act.plan), leg(act.plan, act.send)];
}

/**
 * Where the run's single `state` string belongs: the live or the failed phase, and nowhere else.
 *
 * While a run is live, or once it has died (failed OR unknown — see this module's header), the string
 * describes the phase it is in and nothing else — it is the one place a phase name in the platform's
 * own words ("Creating Unload Data") reaches the screen, and only one row can honestly carry it. The
 * settled phases above it have no state of their own and render nothing rather than borrowing one.
 *
 * A SETTLED RUN therefore gives its state to no row at all. This function used to hand it to all
 * three on the rationale that a finished run's state ("Sync completed") describes the whole thing —
 * true, and the reason it does not belong here: the whole thing already has two places to say so on
 * the same screen. `RunIdentityCard` builds `run.state` into its meta line and `SyncStatusBadge`
 * states the same fact in its own words, one card above these rows. Three phase rows repeating it
 * made four copies of one sentence, and a repeated sentence is not a finer-grained reading.
 */
function stateFor(run: RawSyncRun, activity: PhaseActivity): string {
  // `??` here, not a stylistic `||`: with a `''` fallback the two operators are behaviourally
  // identical (`'' || ''` and `'' ?? ''` both yield `''`; `undefined || ''` and `undefined ?? ''`
  // both yield `''`), so no suppression is needed. Contrast the `||` in `run-fixtures.test.ts`'s
  // "defeats a nullish guard when empty" test — the `falsy` local, which falls back to a NON-EMPTY
  // `'Starting…'`. (Named rather than cited by line: this comment previously pointed at line 207,
  // which is inside that test's own comment; the `||` is at 210 today and will move again.) There
  // `??` and `||` genuinely diverge — a falsy-but-defined value like `0` or `false` passes through
  // `??` and gets overwritten by `||` — and that divergence is the reason to prefer one operator
  // over the other. This site has no such reason.
  const state = run.state ?? '';
  return activity === 'live' || activity === 'failed' ? state : '';
}

/**
 * The phase name in words. Lives here, not in `PhaseStage`, because two surfaces now need the same
 * word for the same phase: the row's own `sr-only` label, and `RunIdentityCard`'s error sentence.
 * Two copies would be two vocabularies the moment one is edited.
 */
export const PHASE_LABEL: Record<PhaseName, string> = {
  extract: 'Extract',
  plan: 'Plan',
  send: 'Send',
};

/**
 * Which phase the run died in, or `null` if it did not die.
 *
 * This is DERIVED, never read from the payload. `run.error` is a single run-level string that names
 * no phase — the one non-empty error across all 17 fixtures is `'task cancelled'` — so the only
 * honest attribution available is the one `activities()` already makes to paint the failed leg and the
 * phase's own announced state. Naming the phase in the error sentence therefore says exactly what the
 * stage already says, and nothing more.
 *
 * Returns `extract` or `send`, never `plan` — `activePhase()` has no `plan` arm, and a Plan-phase
 * failure is not distinguishable on this payload, so it must not be invented here.
 *
 * That claim was FALSE until `activePhase` was fixed to read `metrics.total` directly: `isCounted()`
 * returns `true` for every non-running run, so every failed run resolved to `send` and this function
 * was a constant wearing a derivation's clothes. `extract` is now reachable — a run that stopped
 * having counted nothing — and the tests below pin both arms.
 */
export function failedPhase(run: RawSyncRun): PhaseName | null {
  const act = activities(run);
  const order: PhaseName[] = ['extract', 'plan', 'send'];
  return order.find((p) => act[p] === 'failed') ?? null;
}

export function deriveRunPhases(run: RawSyncRun): [RunPhase, RunPhase, RunPhase] {
  const act = activities(run);
  const counted = isCounted(run);
  // Computed once and shared — see `RunPhase.legIn`/`legOut` for why the two halves of a leg cannot be
  // allowed to derive their own state independently.
  const [legOne, legTwo] = legStates(act);
  // `failedPhase()`'s own predicate, off the `activities()` call already in hand — see
  // `RunPhase.idleKind`. A run with a failed phase has stopped, so anything still idle on it is void.
  const runStopped = act.extract === 'failed' || act.plan === 'failed' || act.send === 'failed';
  const idleKindOf = (activity: PhaseActivity): 'pending' | 'void' | null =>
    activity !== 'idle' ? null : runStopped ? 'void' : 'pending';

  const total = run.metrics?.total ?? 0;
  const invalid = group(run.metrics?.invalid);
  const changed = group(run.metrics?.changed);
  const successful = group(run.metrics?.successful);
  const failed = group(run.metrics?.failed);
  const dropped = group(run.metrics?.dropped);
  const returned = validRecords(run);

  // Whether the PLAN itself was measured. `changed.total === -1` is the platform's
  // count-query-timeout sentinel (`isMeasured`, `run-view.ts`) and it can appear on a SETTLED run,
  // where `counted` (isCounted) is unconditionally true.
  //
  // This is NOT folded into the phase's `counted` flag. It briefly was, and the result was a settled
  // run rendering "counting matched" — `RunDenominated`'s `counted={false}` word is PROGRESSIVE
  // ("still counting, wait"), while a timed-out count query on a finished run is TERMINAL. The
  // numerator guard lives in `RunDenominated`, symmetric with the denominator guard it already had,
  // so `counted` keeps its one meaning (is the RUN measuring yet) and the sentinel is refused once,
  // by the component that owns number-rendering, for both the numerator and the denominator.
  //
  // What it IS still used for, below: gating the breakdown line. That is a separate fact — an
  // unmeasured plan has no inserts/updates/deletes to report — and no component can infer it from
  // the numbers, because `group()` reads every absent sub-key as a plausible-looking 0.
  const planMeasured = isMeasured(changed.total);

  // THE MID-PLAN WINDOW — the blind spot `activePhase`'s doc names, guarded at the one place it can
  // do damage. `metrics.total` is written by extract and `metrics.changed` by the diff that follows,
  // so a RUNNING run polled between the two reads `counted === true` with `changed.total === 0`.
  // Neither existing gate catches it: `counted` is already true and `isMeasured(0)` is true, so the
  // Plan breakdown would print `new 0 · updated 0 · deleted 0` — a three-way partition of a diff
  // that is still running, fabricated out of `group()` reading absent sub-keys as 0. Same failure
  // mode as the `-1` sentinel's, from the opposite direction.
  //
  // Scoped to `isRunning` on purpose, and `total > 0` is load-bearing: `settledNothingToDo` has the
  // identical `changed.total === 0, total: 20` shape and its three zeros are a real, final
  // measurement, which must keep printing. Position is untouched — see `activePhase`'s doc for why
  // that needs producer evidence nobody has.
  const midPlanWindow = isRunning(run) && changed.total === 0 && total > 0;

  const line = (parts: [string, number][]): string =>
    parts.map(([label, value]) => `${label} ${exact(value)}`).join(' · ');

  const extract: RunPhase = {
    phase: 'extract',
    activity: act.extract,
    // WHERE THESE COME FROM, kept here rather than on screen: `n` is `metrics.total −
    // metrics.invalid.total` (that arithmetic lives in `validRecords`, `run-view.ts`, which carries
    // the same note) and `d` is `metrics.total`. The three field paths this deriver reads used to be
    // rendered as a footer under each row; they were cut because a customer cannot act on a payload
    // field path, and Extract's was not even a field name — it was this expression.
    n: returned,
    d: total,
    unit: 'returned',
    // `valid` — the records that survived the null/duplicate filter, and the word Plan's own
    // denominator then repeats.
    numeratorUnit: 'valid',
    counted,
    // CAVEAT, deliberately not on screen: pgreporting/util.go routes DELIVERY aborts here too —
    // HTTP 400 → nulls, HTTP 401 → duplicates. These two counts are NOT purely warehouse-owned, so
    // never attribute them to the customer's data without checking the run had no aborts.
    //
    // Gated on `!counted` too, not only `idle`: on `liveExtract` every metric reads a placeholder 0
    // (`counted` is false — see `isCounted`) and `activity` is `live`, not `idle`, so the idle-only
    // gate rendered `nulls 0 · duplicates 0` for a phase that has measured nothing — "every zero here
    // is a lie the wire tells" per that fixture's own note.
    // Gated on `total > 0` as well: `nulls`/`duplicates` CLASSIFY returned rows, so with no rows
    // returned there is nothing to classify and `group()` reading absent sub-keys as 0 would print a
    // classification that never ran. Two runs reach this: one that stopped before counting (newly
    // reachable since `activePhase` stopped asking `isCounted`, and the reason this guard was added),
    // and a succeeded run whose audience returned no rows at all. Both used to print
    // `nulls 0 · duplicates 0`. Same fabrication class as `midPlanWindow` below, same treatment.
    breakdown:
      act.extract === 'idle' || !counted || total === 0
        ? ''
        : line([
            ['nulls', invalid.nulls ?? 0],
            ['duplicates', invalid.duplicates ?? 0],
          ]),
    state: stateFor(run, act.extract),
    legIn: null,
    legOut: legOne,
    idleKind: idleKindOf(act.extract),
  };

  const plan: RunPhase = {
    phase: 'plan',
    activity: act.plan,
    // `n` is `metrics.changed.total`; `d` is Extract's `n` (see above), so the fraction reads the
    // diff against what the warehouse returned. Passed through even when it is the `-1` sentinel. `RunDenominated` refuses to format a
    // negative NUMERATOR and says {@link PLAN_UNMEASURED} in words instead — exactly the discipline
    // Send's `d` relies on a few lines down, now applied to both ends of the fraction. Sanitising it
    // here would be the deriver taking over a job that component already does.
    n: changed.total,
    d: returned,
    // `valid`, NOT `matched`. This names the DENOMINATOR — the records that survived Extract's
    // null/duplicate filter, i.e. Extract's own numerator word repeated one row down. NOTHING IN THIS
    // PIPELINE MATCHES ANYTHING: Plan is a diff against the previous sync. The old word also collided
    // with "match rate", the term of art for how many identifiers a destination (Meta, TikTok) resolved
    // against its own users — a number this payload does not carry in any field. Worst of all it
    // inverted the sentiment of the MODAL run: `settledNothingToDo` rendered "0 of 1,230 matched ·
    // 0.0%", which reads as the destination rejecting every record and actually means nothing changed
    // since the last sync.
    unit: 'valid',
    numeratorUnit: 'changed',
    counted,
    // Gated on `!planMeasured` as well as `!counted`: an unmeasured plan has no
    // `inserts`/`updates`/`deletes` to report, and `group()` reading an absent sub-key as 0 would
    // otherwise print a fabricated three-way split — `new 0 · updated 0 · deleted 0` — for a plan
    // that was never diffed at all. `midPlanWindow` refuses the same fabrication for a plan that is
    // still being diffed; see its own note above.
    breakdown:
      act.plan === 'idle' || !counted || !planMeasured || midPlanWindow
        ? ''
        : line([
            ['new', changed.inserts ?? 0],
            ['updated', changed.updates ?? 0],
            ['deleted', changed.deletes ?? 0],
          ]),
    state: stateFor(run, act.plan),
    legIn: legOne,
    legOut: legTwo,
    idleKind: idleKindOf(act.plan),
  };

  const send: RunPhase = {
    phase: 'send',
    activity: act.send,
    // `n` is `metrics.successful.total`; `d` is `metrics.changed.total`, i.e. Plan's own `n`.
    n: successful.total,
    // Passed through EVEN IF it is the -1 sentinel. Do not sanitise it to 0 here: that would turn
    // "the plan size was never measured" into "the plan was empty", which is a different and false
    // statement. `RunDenominated` drops the denominator for a negative `d` and says so in words —
    // that is the one place the sentinel is allowed to change what renders.
    d: changed.total,
    unit: 'changed',
    // `accepted`, and this is where a real split in the vocabulary closes. Send's number is
    // `successful.total`, which `run-view.ts` calls "accepted" in five sentences and `RunsPanel` heads
    // its column with (`Changed → Accepted`) — while this row, having only one word to spend, spent it
    // on the DENOMINATOR and so called the same number "changed". Two names for one quantity on one
    // screen. Now the denominator keeps `changed` (it is Plan's number) and the value gets its own.
    numeratorUnit: 'accepted',
    counted,
    // NEVER an operation split: failed and dropped carry no sub-keys in any run, and
    // successful.inserts reads 0 at every intermediate flush — invalid live, misleading settled.
    breakdown:
      act.send === 'idle' || !counted
        ? ''
        : line([
            ['failed', failed.total],
            ['dropped', dropped.total],
          ]),
    state: stateFor(run, act.send),
    legIn: legTwo,
    legOut: null,
    idleKind: idleKindOf(act.send),
  };

  return [extract, plan, send];
}
